import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import {
    GovernanceService,
    type DataExclusionKey,
    type GovernancePolicy,
    type RetentionCategory,
    type RetentionValue,
} from './GovernanceService';

/**
 * Backs Settings → Data & Retention: loads the tenant's governance policy into an
 * editable draft, tracks dirty state, and persists edits. The danger-zone
 * "delete all data" action triggers a data-only purge (collected data removed;
 * the account stays live).
 */
@injectable()
export class GovernanceSettingsViewModel extends BaseViewModel {
    loading = true;
    saving = false;
    error: string | null = null;
    saveError: string | null = null;
    policy: GovernancePolicy | null = null;
    dirty = false;
    purging = false;
    private initialized = false;

    constructor(private readonly service: GovernanceService) {
        super();
    }

    init(): void {
        if (this.initialized) return;
        this.initialized = true;
        void this.load();
    }

    async load(): Promise<void> {
        this.loading = true;
        this.notifyChange();
        try {
            this.policy = await this.service.getPolicy();
            this.error = null;
            this.dirty = false;
        } catch (e) {
            this.error = e instanceof Error ? e.message : 'Failed to load settings';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    setDefaultRetention(value: RetentionValue): void {
        if (!this.policy) return;
        this.policy = { ...this.policy, defaultRetentionDays: value };
        this.markDirty();
    }

    setOverride(category: RetentionCategory, value: RetentionValue): void {
        if (!this.policy) return;
        const overrides = { ...this.policy.retentionOverrides, [category]: value };
        this.policy = { ...this.policy, retentionOverrides: overrides };
        this.markDirty();
    }

    clearOverride(category: RetentionCategory): void {
        if (!this.policy) return;
        const overrides = { ...this.policy.retentionOverrides };
        delete overrides[category];
        this.policy = { ...this.policy, retentionOverrides: overrides };
        this.markDirty();
    }

    toggleExclusion(key: DataExclusionKey): void {
        if (!this.policy) return;
        const exclusions = { ...this.policy.exclusions, [key]: !this.policy.exclusions[key] };
        this.policy = { ...this.policy, exclusions };
        this.markDirty();
    }

    async save(): Promise<boolean> {
        if (!this.policy) return false;
        this.saving = true;
        this.saveError = null;
        this.notifyChange();
        try {
            this.policy = await this.service.updatePolicy({
                defaultRetentionDays: this.policy.defaultRetentionDays,
                retentionOverrides: this.policy.retentionOverrides,
                exclusions: this.policy.exclusions,
            });
            this.dirty = false;
            return true;
        } catch (e) {
            this.saveError = e instanceof Error ? e.message : 'Failed to save';
            return false;
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    async requestPurge(): Promise<boolean> {
        this.purging = true;
        this.notifyChange();
        try {
            await this.service.requestDataPurge();
            return true;
        } catch (e) {
            this.saveError = e instanceof Error ? e.message : 'Failed to start deletion';
            return false;
        } finally {
            this.purging = false;
            this.notifyChange();
        }
    }

    private markDirty(): void {
        this.dirty = true;
        this.notifyChange();
    }
}
